import React from 'react';

import { Button, Modal, MultiSelect, Space } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { TeamService } from 'services/team-service';
import { OWNED_TEAMS_QUERY_KEY, RESOURCES_QUERY_KEY, SMART_ASSISTANTS_QUERY_KEY } from 'shared/constants/query-keys';
import { Resource } from 'shared/interfaces/Resource';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { Team } from 'shared/interfaces/Team';
import {
  MAX_TEAMS_PER_RESOURCE,
  MAX_TEAMS_PER_SMART_ASSISTANT,
  maxStringArrayLengthValidator
} from 'shared/utils/form-validators';

interface SmartAssistantTeamAccessModalProps {
  teamAccessModalOpen: boolean;
  closeTeamAccessModal: () => void;
  selectedTeamFilter: string;
  smartAssistant?: SmartAssistant;
  resource?: Resource;
}

export function TeamAccessModal({
  teamAccessModalOpen,
  closeTeamAccessModal,
  selectedTeamFilter,
  smartAssistant,
  resource
}: SmartAssistantTeamAccessModalProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Mutations
  const updateSmartAssistantMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData(
        [SMART_ASSISTANTS_QUERY_KEY, selectedTeamFilter],
        (existingSmartAssistants: SmartAssistant[] = []) =>
          existingSmartAssistants.map((smartAssistant) =>
            smartAssistant._id === updatedSmartAssistant._id ? updatedSmartAssistant : smartAssistant
          )
      );
      closeTeamAccessModal();
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_TEAM_ACCESS');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_RESOURCES')
  });

  const updateResourceInfoMutation = useMutation({
    mutationFn: ResourceService.updateResourceInfo,
    onSuccess: (updatedResource: Resource) => {
      queryClient.setQueryData([RESOURCES_QUERY_KEY, selectedTeamFilter], (existingResources: Resource[] = []) =>
        existingResources.map((existingResource) =>
          existingResource._id === updatedResource._id ? updatedResource : existingResource
        )
      );
      closeTeamAccessModal();
      NotificationService.showResult(true, 'FEED_BACK.RESOURCES.SUCCESS.UPDATE');
    },
    onError: (error: AxiosError) => {
      NotificationService.showResult(false, 'FEED_BACK.RESOURCES.FAILURE.UPDATE.UNKNOWN', error);
    }
  });

  // Queries
  const ownedTeamsQuery = useQuery({
    queryKey: [OWNED_TEAMS_QUERY_KEY],
    queryFn: TeamService.getOwnedTeams,
    onSuccess: (ownedTeams: Team[]) => {
      teamAccessForm.setValues({
        teamsNames: getTeamNamesFromTeamIds(ownedTeams, resource?.teams || smartAssistant?.teams || [])
      });
    },
    enabled: teamAccessModalOpen
  });

  if (ownedTeamsQuery.isError) return <ErrorAlert />;

  const ownedTeams = ownedTeamsQuery.data || [];

  // Query helpers
  const getTeamIdsFromTeamNames = (ownedTeams: Team[], teamNames: string[]): string[] => {
    if (!ownedTeams) return [];
    return ownedTeams.reduce((teamIds: string[], team: Team) => {
      if (teamNames.includes(team.name)) {
        teamIds.push(team._id);
      }
      return teamIds;
    }, []);
  };

  const getTeamNamesFromTeamIds = (ownedTeams: Team[], teamIds: string[]): string[] => {
    if (!ownedTeams) return [];

    return ownedTeams.reduce((teamNames: string[], team: Team) => {
      if (teamIds.includes(team._id)) {
        teamNames.push(team.name);
      }
      return teamNames;
    }, []);
  };

  // Forms
  const teamAccessForm = useForm({
    initialValues: {
      teamsNames: getTeamNamesFromTeamIds(ownedTeams, resource?.teams || smartAssistant?.teams || [])
    },
    validate: {
      teamsNames: (value) => maxStringArrayLengthValidator(value, MAX_TEAMS_PER_SMART_ASSISTANT)
    }
  });

  return (
    <Modal
      title={t(
        resource
          ? 'ORGANISATION.TEAMS.RESOURCE_TEAM_ACCESS_MODAL.TITLE'
          : 'ORGANISATION.TEAMS.SMART_ASSISTANT_TEAM_ACCESS_MODAL.TITLE'
      )}
      onClose={closeTeamAccessModal}
      opened={teamAccessModalOpen}>
      <MultiSelect
        labelProps={{ style: { fontSize: 16, marginBottom: 5 } }}
        data={ownedTeams?.map((team) => team.name) || []}
        placeholder={t(
          resource
            ? 'ORGANISATION.TEAMS.RESOURCE_TEAM_ACCESS_MODAL.TEAMS_PLACEHOLDER'
            : 'ORGANISATION.TEAMS.SMART_ASSISTANT_TEAM_ACCESS_MODAL.TEAMS_PLACEHOLDER'
        )}
        searchable
        nothingFound={t(
          resource
            ? 'ORGANISATION.TEAMS.RESOURCE_TEAM_ACCESS_MODAL.NO_TEAMS_FOUND'
            : 'ORGANISATION.TEAMS.SMART_ASSISTANT_TEAM_ACCESS_MODAL.NO_TEAMS_FOUND'
        )}
        maxSelectedValues={MAX_TEAMS_PER_RESOURCE}
        limit={20}
        sx={{
          '.mantine-MultiSelect-input': {
            maxHeight: '250px',
            overflowY: 'auto'
          }
        }}
        {...teamAccessForm.getInputProps('teamsNames')}
      />

      <Space h="lg" />

      <Button
        loading={updateSmartAssistantMutation.isLoading}
        onClick={() => {
          teamAccessForm.validate();
          if (teamAccessForm.isValid()) {
            if (resource) {
              updateResourceInfoMutation?.mutate({
                resourceId: resource._id,
                teams: getTeamIdsFromTeamNames(ownedTeams, teamAccessForm.values.teamsNames)
              });
            } else if (smartAssistant) {
              updateSmartAssistantMutation?.mutate({
                id: smartAssistant._id,
                teams: getTeamIdsFromTeamNames(ownedTeams, teamAccessForm.values.teamsNames)
              });
            }
          }
        }}
        style={{
          float: 'right',
          marginBottom: 20
        }}>
        {t('APP.ACTIONS.UPDATE')}
      </Button>
    </Modal>
  );
}
